/**
 * ViewPractitionerModal Component
 *
 * Read-only modal for viewing complete practitioner details.
 */

import React from 'react';
import { Modal } from '@/components/base';
import { StatusBadge } from '@/components/views/TableView';
import { User, Mail, Phone, Briefcase, Stethoscope, Calendar, Hash } from 'lucide-react';

export interface ViewPractitionerData {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  role: string | null;
  specialty: string | null;
  active: boolean;
  qualification?: string | null;
  practitionerRoleId?: string;
}

export interface ViewPractitionerModalProps {
  isOpen: boolean;
  onClose: () => void;
  practitioner: ViewPractitionerData | null;
}

const DetailRow = ({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | null | undefined;
}) => (
  <div className="flex items-start gap-3 py-3">
    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
      <Icon className="h-5 w-5 text-gray-600" />
    </div>
    <div className="flex-1">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-gray-900">{value || '—'}</p>
    </div>
  </div>
);

export function ViewPractitionerModal({
  isOpen,
  onClose,
  practitioner,
}: ViewPractitionerModalProps) {
  if (!practitioner) return null;

  const fullName = `${practitioner.firstName} ${practitioner.lastName}`.trim();

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="md"
      hideCloseButton={false}
    >
      <div className="py-2">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-2xl font-semibold text-white">
            {fullName.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <h2 className="font-hanken text-xl font-semibold text-gray-900">{fullName}</h2>
            <div className="mt-1 flex items-center gap-2">
              <StatusBadge status={practitioner.active ? 'active' : 'inactive'} />
              <span className="text-sm text-gray-500">•</span>
              <span className="text-sm text-gray-600">{practitioner.role}</span>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-1 divide-y divide-gray-100">
          <DetailRow icon={Hash} label="Practitioner ID" value={practitioner.id} />
          <DetailRow icon={User} label="Full Name" value={fullName} />
          <DetailRow icon={Mail} label="Email Address" value={practitioner.email} />
          <DetailRow icon={Phone} label="Phone Number" value={practitioner.phone} />
          <DetailRow icon={Briefcase} label="Role" value={practitioner.role} />
          <DetailRow icon={Stethoscope} label="Specialization" value={practitioner.specialty} />
          {practitioner.qualification && (
            <DetailRow icon={Calendar} label="Qualification" value={practitioner.qualification} />
          )}
          {practitioner.practitionerRoleId && (
            <DetailRow
              icon={Hash}
              label="Practitioner Role ID"
              value={practitioner.practitionerRoleId}
            />
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-lg bg-gray-100 px-6 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
